/**
 * Task system v2 — row and payload types (schema `tasks`).
 *
 * projects        manager-owned containers; progress rolls up from their tasks
 * tasks           the unit of work: assignee, type/department/priority, due date, optional
 *                 verification loop, subtasks (parent_task_id), escalation links, and
 *                 associations to calls / ServiceTitan jobs & customers
 * task_reads      who read a task when — the "is anyone actually looking at this" signal
 * task_comments   discussion thread
 * task_attachments images & files (stored on local disk, streamed with auth)
 * task_types /    editable catalogues
 * task_departments
 * task_schedules  manager-defined one-time or recurring templates; the worker spawns
 *                 real tasks when next_run_at passes
 * user_managers   who escalations route to
 * task_participants extra assignees (shared responsibility) and cc watchers
 */

import { OUTBOUND_CALLS_DEPARTMENT } from '#app/services/outbound/types.ts'

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
export const STATUSES = [
	'open',
	'in_progress',
	'blocked',
	'awaiting_verification',
	'completed',
	'verified',
	'cancelled',
] as const
export const OPEN_STATUSES = [
	'open',
	'in_progress',
	'blocked',
	'awaiting_verification',
] as const
export const DONE_STATUSES = ['completed', 'verified', 'cancelled'] as const

/**
 * The shared "Missed calls" pool (tasks.task_departments id): every missed
 * direct-line call lands here unclaimed, and everyone who takes calls is a
 * member (calls/callback-task.server.ts, calls/missed-calls-pool.server.ts).
 * Its members are never emailed task by task — the pool is in the task panel.
 */
export const MISSED_CALLS_DEPARTMENT = 'missed_calls'

/**
 * The pools whose membership is "everyone who takes calls", kept in step with
 * 3CX every few minutes (calls/missed-calls-pool.server.ts): Missed calls, and
 * Outbound calls (whose members see "Claim Outbound Call" —
 * services/outbound/). A manager's removal from one of these is recorded as an
 * exclusion so the sync does not add the person back, and nobody is emailed
 * task by task about them.
 */
export const CALL_TAKER_POOLS: ReadonlyArray<string> = [
	MISSED_CALLS_DEPARTMENT,
	OUTBOUND_CALLS_DEPARTMENT,
]

export type ProjectRow = {
	id: number
	name: string
	description: string | null
	department: string | null
	status: string
	due_at: Date | null
	created_by: string
	created_at: Date
	updated_at: Date
}

export type TaskRow = {
	id: number
	title: string
	body: string | null
	project_id: number | null
	parent_task_id: number | null
	type_id: string | null
	department: string | null
	priority: string
	status: string
	/** null = an unclaimed department-pool task */
	assignee_email: string | null
	created_by: string
	requires_verification: boolean
	verifier_email: string | null
	due_at: Date | null
	scheduled_for: Date | null
	schedule_id: number | null
	escalated_from_task_id: number | null
	call_id: number | null
	st_job_id: number | null
	st_customer_id: number | null
	completed_at: Date | null
	completed_by: string | null
	verified_at: Date | null
	verified_by: string | null
	created_at: Date
	updated_at: Date
}

export type TaskReadRow = {
	task_id: number
	user_email: string
	read_at: Date
}

export type TaskCommentRow = {
	id: number
	task_id: number
	author: string
	body: string
	kind: string
	created_at: Date
}

export type TaskAttachmentRow = {
	id: number
	task_id: number
	comment_id: number | null
	filename: string
	content_type: string | null
	size_bytes: number
	/** relative to the uploads root */
	storage_path: string
	uploaded_by: string
	created_at: Date
}

export type TaskTypeRow = {
	id: string
	name: string
	active: boolean
	sort: number
}

export type TaskDepartmentRow = {
	id: string
	name: string
	active: boolean
	sort: number
}

export type TaskScheduleRow = {
	id: number
	title: string
	body: string | null
	type_id: string | null
	department: string | null
	priority: string
	assignee_email: string
	requires_verification: boolean
	verifier_email: string | null
	project_id: number | null
	/** due_at = spawn time + offset */
	due_offset_hours: number | null
	/** once | recurring */
	kind: string
	run_at: Date | null
	/** daily | weekly | monthly */
	recur_freq: string | null
	/** weekly: [0..6] Mon=0 */
	recur_days: Array<number> | null
	/** monthly */
	recur_day_of_month: number | null
	/** "HH:MM" America/New_York */
	recur_time: string | null
	next_run_at: Date | null
	last_run_at: Date | null
	active: boolean
	created_by: string
	created_at: Date
	updated_at: Date
}

export type UserManagerRow = {
	user_email: string
	manager_email: string
	updated_by: string | null
	updated_at: Date
}

/**
 * Extra people on a task: additional assignees (shared responsibility) and cc watchers
 * (visibility only). The primary owner stays tasks.tasks.assignee_email.
 */
export type TaskParticipantRow = {
	task_id: number
	user_email: string
	/** assignee | cc */
	role: string
	added_by: string | null
	created_at: Date
}

/** Who belongs to a department's task pool (multi-department membership allowed). */
export type DepartmentMemberRow = {
	department: string
	user_email: string
	added_by: string | null
	added_at: Date
}

// ------------------------------------------------------------------------------------------ payloads

export type TaskIn = {
	title: string
	body?: string | null
	/** null = create the task unassigned, in its department's pool (department required) */
	assignee_email: string | null
	/** additional assignees (shared responsibility) */
	assignees?: Array<string>
	/** watchers - visibility only */
	cc?: Array<string>
	project_id?: number | null
	parent_task_id?: number | null
	type_id?: string | null
	department?: string | null
	priority?: string
	requires_verification?: boolean
	verifier_email?: string | null
	due_at?: string | Date | null
	call_id?: number | null
	st_job_id?: number | null
	st_customer_id?: number | null
}

export type TaskPatch = {
	title?: string | null
	body?: string | null
	assignee_email?: string | null
	project_id?: number | null
	type_id?: string | null
	department?: string | null
	priority?: string | null
	requires_verification?: boolean | null
	verifier_email?: string | null
	due_at?: string | Date | null
	clear_due?: boolean
	st_job_id?: number | null
	st_customer_id?: number | null
	call_id?: number | null
}

export type ProjectIn = {
	name: string
	description?: string | null
	department?: string | null
	status?: string
	due_at?: string | Date | null
}

export type ScheduleIn = {
	title: string
	body?: string | null
	type_id?: string | null
	department?: string | null
	priority?: string
	assignee_email: string
	requires_verification?: boolean
	verifier_email?: string | null
	project_id?: number | null
	due_offset_hours?: number | null
	/** once | recurring */
	kind?: string
	run_at?: string | Date | null
	recur_freq?: string | null
	recur_days?: Array<number> | null
	recur_day_of_month?: number | null
	recur_time?: string | null
	active?: boolean
}
